import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import { formatMinutes, formatMoney } from './perksHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../component/common/designSystem';
import {
  CALL_DURATION_RAMP,
  CALLS_PER_USER_RAMP,
  expiryChipClass,
  expiryLabel,
  schemeStatusChipClass
} from './perksTheme';

/**
 * Perks → Instant Support (EAP).
 *
 * Two halves with different scopes, and the page is laid out to keep them
 * apart:
 *  - THE PLAN is a company-level contract — seats, price, expiry, the number
 *    people actually ring. It is the same for every department.
 *  - THE USAGE is per person and follows the section's department filter.
 *
 * NOTE ON THE TWO CALL FIGURES: the backend reports `totalCalls` (a running
 * rollup, one row per person) and `loggedCalls` (a ledger, one row per call)
 * separately, because on real data they disagree — the rollup predates the
 * ledger. Duration statistics come only from the ledger, so this page says
 * "logged calls" wherever a number is derived from it.
 */
const InstantSupportDashboard = ({ fetcher, populationLabel = 'employees' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const plan = stats?.plan || null;
  const callsPerDay = stats?.callsPerDay || [];
  const minutesPerDay = stats?.minutesPerDay || [];
  const durationDistribution = stats?.durationDistribution || [];
  const callsPerUser = stats?.callsPerUserDistribution || [];
  const weekday = stats?.weekdayCalls || [];
  const planHistory = stats?.planHistory || [];

  const trendLabels = lastNDaysLabels(callsPerDay.length);

  // Share of the population that has ever called.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.callers / stats.totalUsers) * 100) : null;

  /**
   * Calls and minutes on one chart, two axes: a week with many short calls and
   * a week with one long one both move the bars, and only the minutes line
   * separates them.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.blue, CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: [
      { seriesName: 'Calls', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Minutes',
        opposite: true,
        labels: { formatter: (val) => formatBigNumber(val) }
      }
    ],
    tooltip: {
      shared: true,
      // Bar defaults switch intersect on and then refuse to pair it with
      // shared, so it is switched off explicitly wherever shared is used.
      intersect: false,
      y: [
        { formatter: (val) => `${val.toLocaleString()} calls` },
        { formatter: (val) => formatMinutes(val) }
      ]
    }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.blue],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekday.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} calls` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Instant Support | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.plays}
          title="Total Calls"
          value={stats ? formatBigNumber(stats.totalCalls) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="People Calling"
          value={stats ? formatBigNumber(stats.callers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Confirmed"
          value={stats ? `${stats.confirmationRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Time on Calls"
          value={stats ? formatMinutes(stats.totalMinutes) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Avg Call Length"
          value={stats ? formatMinutes(stats.avgCallMinutes) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.seats}
          title="Seats Used"
          value={stats?.seatUtilisation != null ? `${stats.seatUtilisation}%` : '-'}
        />
      </div>

      {/* ---------- The plan ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Panel
          className="lg:col-span-2"
          title="Your EAP Plan"
          subtitle="Company-wide — not narrowed by the department filter"
        >
          {plan ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
              <PlanField label="Plan" value={plan.name} />
              <PlanField label="Type" value={plan.planType} />
              <PlanField
                label="Status"
                value={
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                      plan.status
                    )}`}
                  >
                    {plan.status}
                  </span>
                }
              />
              <PlanField label="Support Number" value={plan.allocatedNumber} />
              <PlanField label="Seats" value={formatBigNumber(plan.seats)} />
              <PlanField label="Billing" value={plan.billingType} />
              <PlanField label="Price per Seat" value={formatMoney(plan.pricePerSeat)} />
              <PlanField label="Total" value={formatMoney(plan.totalAmount)} />
              <PlanField
                label="Payment"
                value={
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                      plan.paymentStatus
                    )}`}
                  >
                    {plan.paymentStatus}
                  </span>
                }
              />
              <PlanField label="Purchased" value={plan.purchaseDate} />
              <PlanField
                label="Expires"
                value={
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${expiryChipClass(
                      plan.daysToExpiry
                    )}`}
                  >
                    {expiryLabel(plan.daysToExpiry)}
                  </span>
                }
              />
              <PlanField label="Auto Renew" value={plan.autoRenew ? 'On' : 'Off'} />
            </div>
          ) : (
            <PanelEmpty>No Instant Support plan is set up for your company.</PanelEmpty>
          )}
        </Panel>

        <BigStatPanel
          title="Support Reach"
          subtitle={`Share of ${populationLabel} who have called`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.callers)} of ${formatBigNumber(
                stats.totalUsers
              )} ${populationLabel}`
              : null
          }
        />
      </div>

      {/* ---------- Distributions ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Call Length"
          subtitle="How long each call ran"
          data={durationDistribution}
          colors={CALL_DURATION_RAMP}
          unit="calls"
          legendLimit={5}
        />
        <MiniDonutCard
          title="Calls per Person"
          subtitle="How often people come back"
          data={callsPerUser}
          colors={CALLS_PER_USER_RAMP}
          unit={populationLabel}
          legendLimit={4}
        />
        {/*
          Confirmation is the support-shaped half: a call that was placed but
          never confirmed is somebody who reached out and did not get through.
        */}
        <Panel title="Call Outcome" subtitle="Placed against confirmed">
          <ProgressList
            items={[
              { label: 'Confirmed', count: stats?.confirmedCalls || 0 },
              {
                label: 'Unconfirmed',
                count: Math.max((stats?.totalCalls || 0) - (stats?.confirmedCalls || 0), 0)
              }
            ]}
            unit="calls"
          />
        </Panel>
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Calls & Time" subtitle="Per day — last 30 days">
          {callsPerDay.some((value) => value > 0) ? (
            <Chart
              type="line"
              height={340}
              width="100%"
              series={[
                { name: 'Calls', type: 'area', data: callsPerDay },
                { name: 'Minutes', type: 'line', data: minutesPerDay }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No support calls in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Calls by Weekday" subtitle="When people reach out">
          {weekday.some((day) => day.calls > 0) ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[{ name: 'Calls', data: weekday.map((day) => day.calls) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No support calls recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Plan history ---------- */}
      {planHistory.length > 0 && (
        <div className="mt-6">
          <Panel title="Plan History" subtitle="Changes to your EAP contract">
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">From</th>
                  <th className="py-2 pr-4 font-medium">To</th>
                  <th className="py-2 font-medium">Reason</th>
                </>
              }
            >
              {planHistory.map((entry, index) => (
                <tr key={`${entry.date}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{entry.date || '-'}</td>
                  <td className="py-2 pr-4">{entry.from || '-'}</td>
                  <td className="py-2 pr-4">
                    {entry.to ? (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                          entry.to
                        )}`}
                      >
                        {entry.to}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2">{entry.reason || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          </Panel>
        </div>
      )}
    </div>
  );
};

/** One label/value pair in the plan card. */
const PlanField = ({ label, value }) => (
  <div>
    <div className="text-xs text-shoorah-gray3">{label}</div>
    <div className="text-sm font-medium text-shoorah-lightBlack dark:text-white mt-0.5">
      {value || '-'}
    </div>
  </div>
);

PlanField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node
};

InstantSupportDashboard.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default InstantSupportDashboard;
